import React from 'react';
import DeprecatedDynamicEntity from 'src/UI/dynamicUI/layout/DeprecatedDynamicEntity';
import DynamicFieldRow from './DynamicFieldRow';
import DynamicField from './DynamicField';
import DynamicEntityList from './DynamicEntityList';
import { Divider, Grid } from '@mantine/core';
import DynamicEntitySection from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import { UiField, UiFieldRow, UiGrid, UiGridColumn, UiItem } from 'src/UI/dynamicUI/types';
import styles from './DynamicEntity.module.css';

interface DynamicLayoutItemProps {
  item: UiItem;
  index?: number;
}

export default function DynamicLayoutItem({ item, index }: DynamicLayoutItemProps) {
  const itemType = item.type;

  if (itemType === 'UI_ENTITY') {
    return <DeprecatedDynamicEntity section={item} />;
  }

  if (itemType === 'UI_ENTITY_SECTION_GROUP') {
    return <DynamicEntitySection section={item} />;
  }

  if (itemType === 'FIELD_ROW') {
    return <DynamicFieldRow fieldRow={item} index={index} />;
  }

  if (itemType === 'FIELD') {
    return <DynamicField field={item} index={index} />;
  }

  if (itemType === 'SEPARATOR') {
    return <Divider data-testid="divider" />;
  }

  if (itemType === 'ENTITY_LIST') {
    return <DynamicEntityList entityList={item} />;
  }

  if (itemType === 'UI_GRID') {
    return <DynamicGrid grid={item} />;
  }

  // Ensure we're handling all possible item types
  itemType satisfies never;

  return null;
}

interface DynamicGridProps {
  grid: UiGrid;
}

function DynamicGrid({ grid }: DynamicGridProps) {
  const renderColumnContent = (item: UiGridColumn | UiField | UiFieldRow, parentIndex: number) => {
    const isUiField = item.type === 'FIELD';
    return isUiField ? (
      <DynamicLayoutItem key={`${parentIndex}`} item={item} index={parentIndex} />
    ) : (
      item.children.map((child, i) => (
        <DynamicLayoutItem key={`${parentIndex}-${i}`} item={child} index={i} />
      ))
    );
  };

  const renderColumns = (items: (UiGridColumn | UiField | UiFieldRow)[]) => {
    return items.map((item, index) => {
      const className = grid.isHighlighted ? styles.highlightedColumn : undefined;
      return (
        <Grid.Col p={'sm'} className={className} key={index} span={item.span}>
          {renderColumnContent(item, index)}
        </Grid.Col>
      );
    });
  };

  return <Grid>{renderColumns(grid.children)}</Grid>;
}
